import { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Eye, EyeSlash, PaperPlaneRight, CalendarBlank, MagnifyingGlass, X, Flag, Lock, Paperclip, DownloadSimple, CaretRight, CaretDown, Archive, Trash } from "@phosphor-icons/react";
import DependencyGraph from "@/components/DependencyGraph";
import TaskChatPanel from "@/components/TaskChatPanel";
import UserAvatar from "@/components/UserAvatar";
import { getTaskActionPermissions } from "@/lib/taskPermissions";

const statusLabels = {
  todo: { label: "To Do", color: "#0055FF" },
  in_progress: { label: "In Progress", color: "#D97706" },
  review: { label: "Review", color: "#7C3AED" },
  done: { label: "Done", color: "#16A34A" },
};

const priorityLabels = {
  high: { label: "High", style: "bg-[#FEF2F2] text-[#DC2626]" },
  medium: { label: "Medium", style: "bg-[#FFFBEB] text-[#D97706]" },
  low: { label: "Low", style: "bg-[#F5F5F5] text-[#A3A3A3]" },
};

// ── Dependencies picker ─────────────────────────────────────────────────────
// Compact search + chip-list. No scroll-inside-scroll hunt.
function DependencyPicker({ allTasks, currentTaskId, value, onChange }) {
  const [q, setQ] = useState("");
  const selectedSet = useMemo(() => new Set(value), [value]);
  const selectedTasks = useMemo(
    () => allTasks.filter(t => selectedSet.has(t.id)),
    [allTasks, selectedSet]
  );
  const candidates = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return allTasks
      .filter(t => t.id !== currentTaskId && !selectedSet.has(t.id))
      .filter(t => !needle || (t.title || "").toLowerCase().includes(needle))
      .slice(0, 40);
  }, [allTasks, currentTaskId, selectedSet, q]);

  const toggle = (id, on) => {
    onChange(on ? [...value, id] : value.filter(v => v !== id));
  };

  return (
    <div className="space-y-2" data-testid="task-dependency-selector">
      {selectedTasks.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedTasks.map(t => (
            <span
              key={t.id}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#EFF6FF] text-[#0055FF] text-[11px] font-semibold"
              data-testid={`selected-dep-${t.id}`}
            >
              {t.title}
              <button
                type="button"
                onClick={() => toggle(t.id, false)}
                className="hover:text-[#DC2626]"
                title="Remove"
              >
                <X size={10} weight="bold" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="relative">
        <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#A3A3A3]" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search tasks to add as dependency…"
          className="rounded-none border-[#E5E5E5] h-8 pl-7 text-xs"
          data-testid="dep-search"
        />
      </div>
      {(q.trim() || candidates.length > 0) && (
        <div className="border border-[#E5E5E5] max-h-40 overflow-y-auto">
          {candidates.length === 0 ? (
            <p className="text-[11px] text-[#A3A3A3] p-2">No matching tasks.</p>
          ) : (
            <div className="grid grid-cols-2 gap-0">
              {candidates.map(t => {
                const sCfg = statusLabels[t.status] || statusLabels.todo;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => toggle(t.id, true)}
                    className="flex items-center gap-2 px-2 py-1 text-left text-xs hover:bg-[#EFF6FF] border-b border-r border-[#F5F5F5]"
                    data-testid={`dep-option-${t.id}`}
                  >
                    <span className="w-1.5 h-1.5 shrink-0" style={{ backgroundColor: sCfg.color }} />
                    <span className="truncate text-[#0A0A0A]">{t.title}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function TaskDetailModal({ task, user, projectId, canUpdateProject, onClose, onUpdate, allTasks = [] }) {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [commentVisibility, setCommentVisibility] = useState("public");
  const [editing, setEditing] = useState(user?.user_type === "internal");
  const [form, setForm] = useState({});
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedDeps, setSelectedDeps] = useState([]);
  // v1.55 — Dependencies block is collapsed by default per Mike's
  // request. Operators can click the header to expand it.
  const [depsExpanded, setDepsExpanded] = useState(false);
  // v1.55 — files staged for upload alongside the next new comment.
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  useEffect(() => {
    if (task) {
      setForm({
        title: task.title || "",
        description: task.description || "",
        status: task.status || "todo",
        priority: task.priority || "medium",
        assignee_id: task.assignee_id || "",
        start_date: task.start_date || "",
        end_date: task.end_date || "",
        duration_days: task.duration_days || "",
        ball_with: task.ball_with || "Eyvo",
        is_showstopper: !!task.is_showstopper,
        visibility: task.visibility || "public",
      });
      setSelectedDeps(task.dependencies || []);
      loadComments();
      api.get("/users").then(res => setTeamMembers(res.data)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.id]);

  const loadComments = async () => {
    try {
      const res = await api.get(`/tasks/${task.id}/comments`);
      setComments(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSave = async () => {
    try {
      const payload = { ...form, dependencies: selectedDeps };
      if (payload.assignee_id === "unassigned") payload.assignee_id = "";
      if (payload.duration_days === "" || payload.duration_days === null) delete payload.duration_days;
      if (payload.order === "" || payload.order === null) delete payload.order;
      if (payload.start_date === "") delete payload.start_date;
      if (payload.end_date === "") delete payload.end_date;
      await api.put(`/tasks/${task.id}`, payload);
      toast.success("Task updated");
      onUpdate?.();
    } catch (err) {
      toast.error("Failed to update task");
    }
  };

  const handleArchive = async () => {
    try {
      await api.put(`/tasks/${task.id}/archive`);
      toast.success("Task archived");
      setConfirmAction(null);
      onUpdate?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Archive failed");
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/tasks/${task.id}`);
      toast.success("Task deleted");
      setConfirmAction(null);
      onUpdate?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() && pendingFiles.length === 0) return;
    try {
      setUploading(true);
      // 1) Create the comment.
      const res = await api.post(`/tasks/${task.id}/comments`, {
        content: newComment || "(attachment)",
        visibility: commentVisibility,
      });
      const commentId = res.data.id;
      // 2) Upload any staged files, one at a time, against that
      //    comment id. We surface per-file failures but don't roll
      //    the comment back — the comment text is still useful.
      let failed = 0;
      for (const f of pendingFiles) {
        const fd = new FormData();
        fd.append("file", f);
        try {
          await api.post(
            `/tasks/${task.id}/comments/${commentId}/attachments`,
            fd,
            { headers: { "Content-Type": "multipart/form-data" } },
          );
        } catch (err) {
          failed += 1;
          toast.error(`Failed to attach ${f.name}: ${err.response?.data?.detail || err.message}`);
        }
      }
      setNewComment("");
      setPendingFiles([]);
      loadComments();
      if (failed === 0) toast.success(pendingFiles.length ? "Comment + files added" : "Comment added");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add comment");
    } finally {
      setUploading(false);
    }
  };

  // v1.55 — attachment download. Backend streams the file with the
  // correct Content-Disposition so the browser saves it.
  const downloadAttachment = async (comment, att) => {
    try {
      const r = await api.get(
        `/tasks/${task.id}/comments/${comment.id}/attachments/${att.id}/download`,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = att.file_name || "attachment";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Download failed");
    }
  };

  const onFilePick = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setPendingFiles(prev => [...prev, ...files]);
    e.target.value = "";  // allow re-picking the same file later
  };

  const removePendingFile = (idx) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const fmtBytes = (n) => {
    const v = Number(n) || 0;
    if (v < 1024) return v + " B";
    if (v < 1024 * 1024) return (v / 1024).toFixed(1) + " KB";
    return (v / (1024 * 1024)).toFixed(1) + " MB";
  };

  const status = statusLabels[form.status] || statusLabels.todo;
  const priority = priorityLabels[form.priority] || priorityLabels.medium;

  return (
    <Dialog open={!!task} onOpenChange={() => onClose()}>
      <DialogContent
        className="rounded-none border-[#E5E5E5] p-0 gap-0 max-w-[1200px] w-[95vw] h-[90vh] flex flex-col overflow-hidden"
        data-testid="task-detail-modal"
      >
        {/* Header (full width) */}
        <DialogHeader className="px-6 py-4 border-b border-[#E5E5E5] shrink-0">
          <div className="flex items-center gap-3">
            <DialogTitle className="font-bold tracking-tight text-lg flex-1 pr-8" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {editing ? (
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="rounded-none border-[#E5E5E5] h-9 font-bold text-lg"
                  data-testid="task-edit-title"
                />
              ) : (
                <span data-testid="task-title">{task?.title}</span>
              )}
            </DialogTitle>
          </div>
        </DialogHeader>

        {/* Two-column body: details left, chat right */}
        <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[1fr_420px] overflow-hidden">
          {/* LEFT: details + dependencies + comments */}
          <div className="overflow-y-auto px-6 py-5 space-y-5 border-r border-[#E5E5E5]">
            {/* Status & meta */}
            <div className="flex items-center gap-3 flex-wrap">
              {task?.is_showstopper && (
                <Badge
                  className="bg-[#DC2626] text-white border-0 px-2 py-1 text-[10px] font-bold uppercase rounded-none inline-flex items-center gap-1"
                  data-testid="task-showstopper-chip"
                  title="Showstopper — blocking project go-live"
                >
                  <Flag size={10} weight="fill" /> Showstopper
                </Badge>
              )}
              {task?.visibility === "private" && (
                <Badge
                  className="bg-[#6D28D9] text-white border-0 px-2 py-1 text-[10px] font-bold uppercase rounded-none inline-flex items-center gap-1"
                  data-testid="task-private-chip"
                  title="Private — hidden from the client"
                >
                  <Lock size={10} weight="fill" /> Private
                </Badge>
              )}
              <Badge className="rounded-none border-0 px-2 py-1 text-[10px] font-bold uppercase" style={{ backgroundColor: status.color + "15", color: status.color }}>
                {status.label}
              </Badge>
              <Badge className={`${priority.style} rounded-none border-0 px-2 py-1 text-[10px] font-bold uppercase`}>
                {priority.label}
              </Badge>
              {task?.assignee_name && (
                <span className="flex items-center gap-1.5" data-testid="task-detail-assignee">
                  <UserAvatar
                    name={task.assignee_name}
                    avatar={task.assignee_avatar}
                    size="xs"
                  />
                  <span className="text-xs text-[#525252]">Assigned to {task.assignee_name}</span>
                </span>
              )}
              {task?.start_date && (
                <span className="text-xs text-[#A3A3A3] flex items-center gap-1">
                  <CalendarBlank size={11} />
                  {task.start_date} - {task.end_date || "TBD"}
                </span>
              )}
              {/* Ball-With indicator. Eyvo-only edit (Feb 2026 product
                  direction): clients see who currently owns the ball
                  but cannot flip it. */}
              <div className="ml-auto flex items-center gap-2">
                <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#525252]">
                  Ball with
                </Label>
                {user?.user_type === "client" ? (
                  <span
                    className={`inline-flex items-center h-7 px-2 text-[11px] font-bold border min-w-[120px] justify-center ${
                      (form.ball_with || task?.ball_with) === "Client"
                        ? "border-[#DC2626] bg-[#FEE2E2] text-[#991B1B]"
                        : "border-[#E5E5E5] bg-white text-[#525252]"
                    }`}
                    title="Only Eyvo can change who has the ball."
                    data-testid="task-ball-with-readonly"
                  >
                    {(form.ball_with || task?.ball_with) === "Client"
                      ? (task?.client_short_name || "Client")
                      : "Eyvo"}
                  </span>
                ) : (
                  <Select
                    value={form.ball_with || task?.ball_with || "Eyvo"}
                    onValueChange={async (v) => {
                      setForm({ ...form, ball_with: v });
                      try {
                        await api.put(`/tasks/${task.id}`, { ball_with: v });
                        toast.success(`Ball moved to ${v === "Client" ? (task?.client_short_name || "Client") : "Eyvo"}`);
                        onUpdate?.();
                      } catch (err) {
                        toast.error("Failed to move the ball");
                      }
                    }}
                  >
                    <SelectTrigger
                      className={`rounded-none border h-7 text-[11px] px-2 w-auto min-w-[120px] ${
                        (form.ball_with || task?.ball_with) === "Client"
                          ? "border-[#DC2626] bg-[#FEE2E2] text-[#991B1B] font-bold"
                          : "border-[#E5E5E5]"
                      }`}
                      data-testid="task-ball-with-toggle"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="Eyvo">Eyvo</SelectItem>
                      <SelectItem value="Client">{task?.client_short_name || "Client"}</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            {editing ? (
              <div className="space-y-3 border border-[#E5E5E5] p-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Description</Label>
                  <Textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="rounded-none border-[#E5E5E5] min-h-[80px]"
                    data-testid="task-edit-description"
                  />
                </div>

                {/* Status / Priority / Assignee on one row for less vertical scroll */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Status</Label>
                    <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                      <SelectTrigger className="rounded-none border-[#E5E5E5] h-9" data-testid="task-edit-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        {Object.entries(statusLabels).map(([k, v]) => (
                          <SelectItem key={k} value={k}>{v.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Priority</Label>
                    <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                      <SelectTrigger className="rounded-none border-[#E5E5E5] h-9" data-testid="task-edit-priority">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="low">Low</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Assignee</Label>
                    <Select value={form.assignee_id || "unassigned"} onValueChange={v => setForm({ ...form, assignee_id: v })}>
                      <SelectTrigger className="rounded-none border-[#E5E5E5] h-9" data-testid="task-edit-assignee">
                        <SelectValue placeholder="Unassigned" />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {teamMembers.map(m => (
                          <SelectItem key={m.id} value={m.id}>{m.name} ({m.role?.replace(/_/g, " ")})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">
                    Ball With
                    {form.ball_with === "Client" && (
                      <span className="ml-2 inline-block px-1.5 py-0.5 bg-[#FEE2E2] text-[#991B1B] text-[9px] font-bold uppercase">
                        action needed from client
                      </span>
                    )}
                  </Label>
                  <Select value={form.ball_with || "Eyvo"} onValueChange={v => setForm({ ...form, ball_with: v })}>
                    <SelectTrigger className="rounded-none border-[#E5E5E5] h-9" data-testid="task-edit-ball-with">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="Eyvo">Eyvo</SelectItem>
                      <SelectItem value="Client">{task?.client_short_name || "Client"}</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-[#A3A3A3]">
                    When the ball is with the client, the task highlights pastel red across all views.
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Start</Label>
                    <Input type="date" value={form.start_date} onChange={e => {
                      const sd = e.target.value;
                      const updates = { start_date: sd };
                      if (sd && form.duration_days) {
                        const d = new Date(sd);
                        d.setDate(d.getDate() + parseInt(form.duration_days));
                        updates.end_date = d.toISOString().split('T')[0];
                      } else if (sd && !form.duration_days) {
                        const d = new Date(sd);
                        d.setDate(d.getDate() + 7);
                        updates.end_date = d.toISOString().split('T')[0];
                        updates.duration_days = 7;
                      }
                      setForm(prev => ({ ...prev, ...updates }));
                    }} className="rounded-none border-[#E5E5E5] h-9" data-testid="task-edit-start" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Duration (days)</Label>
                    <Input type="number" min={1} value={form.duration_days || ""} onChange={e => {
                      const raw = e.target.value;
                      const dur = raw === "" ? "" : parseInt(raw);
                      const updates = { duration_days: dur };
                      if (form.start_date && dur > 0) {
                        const d = new Date(form.start_date);
                        d.setDate(d.getDate() + dur);
                        updates.end_date = d.toISOString().split('T')[0];
                      }
                      setForm(prev => ({ ...prev, ...updates }));
                    }} className="rounded-none border-[#E5E5E5] h-9" placeholder="7" data-testid="task-edit-duration" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">End</Label>
                    <Input type="date" value={form.end_date} onChange={e => {
                      const ed = e.target.value;
                      const updates = { end_date: ed };
                      if (form.start_date && ed) {
                        const sd = new Date(form.start_date);
                        const endD = new Date(ed);
                        const diff = Math.round((endD - sd) / (1000 * 60 * 60 * 24));
                        updates.duration_days = diff > 0 ? diff : 0;
                      }
                      setForm(prev => ({ ...prev, ...updates }));
                    }} className="rounded-none border-[#E5E5E5] h-9" data-testid="task-edit-end" />
                  </div>
                </div>

                {/* Dependencies — searchable chip picker. v1.55: now
                    collapsed by default per Mike's request — most
                    tasks don't have deps, so this block was just
                    visual noise on first open. Click the header to
                    expand. */}
                {allTasks.length > 0 && (
                  <div className="space-y-1.5">
                    <button
                      type="button"
                      onClick={() => setDepsExpanded(v => !v)}
                      className="w-full flex items-center gap-2 text-left hover:bg-[#FAFAFA] -mx-2 px-2 py-1 transition-colors"
                      data-testid="deps-toggle-edit"
                      title="Show / hide task dependencies"
                    >
                      {depsExpanded ? <CaretDown size={12} weight="bold" className="text-[#525252]" /> : <CaretRight size={12} weight="bold" className="text-[#525252]" />}
                      <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252] cursor-pointer flex-1">
                        Dependencies (blocks this task) · {selectedDeps.length} selected
                      </Label>
                    </button>
                    {depsExpanded && (
                      <>
                        <DependencyPicker
                          allTasks={allTasks}
                          currentTaskId={task.id}
                          value={selectedDeps}
                          onChange={setSelectedDeps}
                        />
                        {selectedDeps.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-[#F5F5F5]">
                            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3] block mb-1">Dependency Chain</span>
                            <DependencyGraph tasks={allTasks} focusTaskId={task.id} mini={true} />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                <div className="flex gap-2 pt-2 flex-wrap">
                  {user?.user_type === "internal" && (
                    <label
                      className={`flex items-center gap-2 border px-2.5 py-2 cursor-pointer transition-colors mr-auto h-9 ${
                        form.is_showstopper
                          ? "bg-[#FEE2E2] border-[#DC2626]"
                          : "bg-white border-[#E5E5E5] hover:border-[#A3A3A3]"
                      }`}
                      data-testid="task-edit-showstopper-label"
                      title="Mark as a project showstopper. Appears at the top of the client's daily digest in red."
                    >
                      <input
                        type="checkbox"
                        checked={!!form.is_showstopper}
                        onChange={(e) => setForm({ ...form, is_showstopper: e.target.checked })}
                        className="accent-[#DC2626]"
                        data-testid="task-edit-showstopper-checkbox"
                      />
                      <Flag size={12} weight={form.is_showstopper ? "fill" : "regular"} className={form.is_showstopper ? "text-[#DC2626]" : "text-[#525252]"} />
                      <span className={`text-[11px] font-bold uppercase tracking-[0.12em] ${form.is_showstopper ? "text-[#991B1B]" : "text-[#525252]"}`}>
                        Showstopper
                      </span>
                    </label>
                  )}
                  {user?.user_type === "internal" && (
                    <label
                      className={`flex items-center gap-2 border px-2.5 py-2 cursor-pointer transition-colors h-9 ${
                        form.visibility === "private"
                          ? "bg-[#F5F3FF] border-[#6D28D9]"
                          : "bg-white border-[#E5E5E5] hover:border-[#A3A3A3]"
                      }`}
                      data-testid="task-edit-visibility-label"
                      title="Hide this task from the client. Only Eyvo internal staff will see it."
                    >
                      <input
                        type="checkbox"
                        checked={form.visibility === "private"}
                        onChange={(e) => setForm({ ...form, visibility: e.target.checked ? "private" : "public" })}
                        className="accent-[#6D28D9]"
                        data-testid="task-edit-visibility-checkbox"
                      />
                      <Lock size={12} weight={form.visibility === "private" ? "fill" : "regular"} className={form.visibility === "private" ? "text-[#6D28D9]" : "text-[#525252]"} />
                      <span className={`text-[11px] font-bold uppercase tracking-[0.12em] ${form.visibility === "private" ? "text-[#5B21B6]" : "text-[#525252]"}`}>
                        Private
                      </span>
                    </label>
                  )}
                  <Button onClick={handleSave} className="bg-[#0055FF] hover:bg-[#0040CC] text-white font-bold rounded-none h-9 px-4 text-xs" data-testid="task-save-button">
                    Save Changes
                  </Button>
                  <Button variant="outline" onClick={onClose} className="rounded-none border-[#E5E5E5] font-bold h-9 px-4 text-xs" data-testid="task-cancel-edit">
                    Close
                  </Button>
                  {task?.assignee_id && user?.user_type === "internal" && (
                    <Button
                      variant="outline"
                      onClick={async () => {
                        if (!window.confirm(`Send a reminder email to ${task.assignee_name || "the assignee"}?`)) return;
                        try {
                          await api.post(`/tasks/${task.id}/resend-notification`);
                          toast.success(`Reminder sent to ${task.assignee_name || "assignee"}`);
                        } catch (e) {
                          toast.error(e.response?.data?.detail || "Failed to send reminder");
                        }
                      }}
                      className="rounded-none border-[#E5E5E5] font-bold h-9 px-4 text-xs"
                      data-testid="task-resend-reminder"
                      title="Send a reminder email to the current assignee"
                    >
                      Send Reminder
                    </Button>
                  )}
                  {(() => {
                    const { canArchive, canDelete } = getTaskActionPermissions(task, user, canUpdateProject);
                    if (!canArchive && !canDelete) return null;
                    return (
                      <div className="flex gap-2 pt-2 border-t border-[#F5F5F5] flex-wrap">
                        {canArchive && !task?.is_archived && !confirmAction && (
                          <Button variant="outline" size="sm" onClick={() => setConfirmAction("archive")} className="rounded-none border-[#E5E5E5] text-[#525252] font-bold h-8 px-3 text-xs" data-testid="task-archive-btn">
                            <Archive size={12} className="mr-1" />Archive
                          </Button>
                        )}
                        {canDelete && !confirmAction && (
                          <Button variant="outline" size="sm" onClick={() => setConfirmAction("delete")} className="rounded-none border-[#DC2626] text-[#DC2626] hover:bg-[#FEF2F2] font-bold h-8 px-3 text-xs" data-testid="task-delete-btn">
                            <Trash size={12} className="mr-1" />Delete
                          </Button>
                        )}
                        {confirmAction === "archive" && (
                          <div className="flex items-center gap-2 w-full">
                            <span className="text-xs text-[#525252]">Archive this task?</span>
                            <Button size="sm" onClick={handleArchive} className="rounded-none bg-[#525252] hover:bg-[#0A0A0A] text-white font-bold h-8 px-3 text-xs" data-testid="confirm-archive-modal">Confirm Archive</Button>
                            <Button variant="outline" size="sm" onClick={() => setConfirmAction(null)} className="rounded-none h-8 px-3 text-xs">Cancel</Button>
                          </div>
                        )}
                        {confirmAction === "delete" && (
                          <div className="flex items-center gap-2 w-full">
                            <span className="text-xs text-[#DC2626] font-medium">Permanently delete? This cannot be undone.</span>
                            <Button size="sm" onClick={handleDelete} className="rounded-none bg-[#DC2626] hover:bg-[#B91C1C] text-white font-bold h-8 px-3 text-xs" data-testid="confirm-delete-modal">Delete Forever</Button>
                            <Button variant="outline" size="sm" onClick={() => setConfirmAction(null)} className="rounded-none h-8 px-3 text-xs">Cancel</Button>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            ) : (
              <>
                {task?.description && (
                  <p className="text-sm text-[#525252] leading-relaxed">{task.description}</p>
                )}
                {task?.dependencies?.length > 0 && (
                  <div className="space-y-2">
                    {/* v1.55 — collapsed by default in read mode too. */}
                    <button
                      type="button"
                      onClick={() => setDepsExpanded(v => !v)}
                      className="w-full flex items-center gap-2 text-left hover:bg-[#FAFAFA] -mx-2 px-2 py-1 transition-colors"
                      data-testid="deps-toggle-read"
                      title="Show / hide task dependencies"
                    >
                      {depsExpanded ? <CaretDown size={12} weight="bold" className="text-[#525252]" /> : <CaretRight size={12} weight="bold" className="text-[#525252]" />}
                      <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#A3A3A3] flex-1">
                        Depends on · {task.dependencies.length}
                      </span>
                    </button>
                    {depsExpanded && (
                      <>
                        <div className="flex flex-wrap gap-1">
                          {task.dependencies.map(depId => {
                            const depTask = allTasks.find(t => t.id === depId);
                            return (
                              <Badge key={depId} className="bg-[#F5F5F5] text-[#525252] border-0 text-[10px] font-medium px-2 py-0.5 rounded-none" data-testid={`dep-badge-${depId}`}>
                                {depTask?.title || depId.slice(0, 8)}
                              </Badge>
                            );
                          })}
                        </div>
                        <div className="mt-2 pt-2 border-t border-[#F5F5F5]">
                          <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3] block mb-1">Dependency Chain</span>
                          <DependencyGraph tasks={allTasks} focusTaskId={task.id} mini={true} />
                        </div>
                      </>
                    )}
                  </div>
                )}
                {user?.user_type === "internal" && (
                  <Button
                    variant="outline"
                    onClick={() => setEditing(true)}
                    className="rounded-none border-[#E5E5E5] font-bold h-9 px-4 text-xs uppercase tracking-wider"
                    data-testid="task-edit-button"
                  >
                    Edit Task
                  </Button>
                )}
              </>
            )}

            <Separator className="bg-[#E5E5E5]" />

            {/* Comments */}
            <div>
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-3">
                Comments ({comments.length})
              </h3>
              <div className="space-y-3 mb-4 max-h-60 overflow-y-auto">
                {comments.map((c) => (
                  <div
                    key={c.id}
                    className={`p-3 ${
                      c.visibility === "private"
                        ? "bg-[#F5F5F5] border-l-2 border-l-[#A3A3A3]"
                        : "bg-white border border-[#E5E5E5] border-l-2 border-l-[#0055FF]"
                    }`}
                    data-testid={`comment-${c.id}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-semibold text-[#0A0A0A]">{c.author_name}</span>
                      <Badge className={`text-[8px] font-bold uppercase px-1.5 py-0 rounded-none border-0 ${
                        c.visibility === "private"
                          ? "bg-[#E5E5E5] text-[#525252]"
                          : "bg-[#0055FF]/10 text-[#0055FF]"
                      }`}>
                        {c.visibility === "private" ? (
                          <><EyeSlash size={8} className="mr-0.5" /> Internal</>
                        ) : (
                          <><Eye size={8} className="mr-0.5" /> Public</>
                        )}
                      </Badge>
                      <span className="text-[10px] text-[#A3A3A3] ml-auto">
                        {new Date(c.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-[#525252] whitespace-pre-wrap">{c.content}</p>
                    {/* v1.55 — attachments on this comment, if any. */}
                    {c.attachments && c.attachments.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-[#F5F5F5] space-y-1" data-testid={`comment-${c.id}-attachments`}>
                        {c.attachments.map(att => (
                          <button
                            key={att.id}
                            type="button"
                            onClick={() => downloadAttachment(c, att)}
                            className="w-full flex items-center gap-2 text-left px-2 py-1.5 bg-[#FAFBFF] hover:bg-[#EFF6FF] border border-[#E5E5E5] hover:border-[#0055FF] transition-colors group"
                            data-testid={`download-attachment-${att.id}`}
                            title={`Download ${att.file_name}`}
                          >
                            <Paperclip size={12} className="text-[#525252] flex-shrink-0" />
                            <span className="text-xs text-[#0A0A0A] font-medium truncate flex-1">{att.file_name}</span>
                            <span className="text-[10px] text-[#A3A3A3] flex-shrink-0">{fmtBytes(att.file_size)}</span>
                            <DownloadSimple size={12} className="text-[#A3A3A3] group-hover:text-[#0055FF] flex-shrink-0" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {comments.length === 0 && (
                  <p className="text-xs text-[#A3A3A3] py-2">No comments yet</p>
                )}
              </div>

              <div className="space-y-2">
                <Textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Write a comment..."
                  className="rounded-none border-[#E5E5E5] min-h-[60px]"
                  data-testid="comment-input"
                />
                {/* v1.55 — staged files preview. Operators (and
                    crucially clients) can attach multiple files to
                    their next comment, e.g. data dumps, screenshots,
                    signed PDFs. */}
                {pendingFiles.length > 0 && (
                  <div className="space-y-1 border border-dashed border-[#BFDBFE] bg-[#EFF6FF] p-2" data-testid="staged-files">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-[#1E3A8A]">
                      {pendingFiles.length} file{pendingFiles.length !== 1 ? "s" : ""} attached to this comment
                    </div>
                    {pendingFiles.map((f, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs bg-white border border-[#E5E5E5] px-2 py-1">
                        <Paperclip size={11} className="text-[#525252]" />
                        <span className="flex-1 truncate">{f.name}</span>
                        <span className="text-[10px] text-[#A3A3A3]">{fmtBytes(f.size)}</span>
                        <button
                          type="button"
                          onClick={() => removePendingFile(i)}
                          className="text-[#A3A3A3] hover:text-[#DC2626]"
                          title="Remove"
                          data-testid={`remove-staged-file-${i}`}
                        >
                          <X size={11} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  {user?.user_type === "internal" && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setCommentVisibility("public")}
                        className={`flex items-center gap-1 px-2 py-1 text-xs font-bold uppercase tracking-wider transition-colors ${
                          commentVisibility === "public"
                            ? "bg-[#0055FF]/10 text-[#0055FF]"
                            : "text-[#A3A3A3] hover:text-[#525252]"
                        }`}
                        data-testid="comment-visibility-public"
                      >
                        <Eye size={12} /> Public
                      </button>
                      <button
                        onClick={() => setCommentVisibility("private")}
                        className={`flex items-center gap-1 px-2 py-1 text-xs font-bold uppercase tracking-wider transition-colors ${
                          commentVisibility === "private"
                            ? "bg-[#F5F5F5] text-[#525252]"
                            : "text-[#A3A3A3] hover:text-[#525252]"
                        }`}
                        data-testid="comment-visibility-private"
                      >
                        <EyeSlash size={12} /> Internal
                      </button>
                    </div>
                  )}
                  {/* v1.55 — attach file. Available to BOTH internal
                      and client users, since the whole point of this
                      change is to let clients send us files. */}
                  <label
                    className="inline-flex items-center gap-1.5 px-3 h-9 border border-[#E5E5E5] hover:border-[#0055FF] text-xs font-bold uppercase tracking-wider text-[#525252] hover:text-[#0055FF] cursor-pointer transition-colors"
                    data-testid="attach-file-btn"
                    title="Attach files to this comment (25 MB max each)"
                  >
                    <Paperclip size={12} />
                    Attach
                    <input
                      type="file"
                      multiple
                      onChange={onFilePick}
                      className="hidden"
                      data-testid="attach-file-input"
                    />
                  </label>
                  <Button
                    onClick={handleAddComment}
                    disabled={uploading || (!newComment.trim() && pendingFiles.length === 0)}
                    className="bg-[#0055FF] hover:bg-[#0040CC] text-white font-bold rounded-none h-9 px-4 text-xs ml-auto"
                    data-testid="submit-comment-button"
                  >
                    <PaperPlaneRight size={12} weight="fill" className="mr-1.5" />
                    {uploading ? "Sending…" : "Send"}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: vertical chat panel */}
          {task?.id && (
            <aside className="hidden lg:flex flex-col min-h-0 bg-[#FAFAFA]">
              <div className="px-4 py-3 border-b border-[#E5E5E5] bg-white shrink-0">
                <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">
                  Task Chat
                </h3>
                <p className="text-[10px] text-[#A3A3A3] mt-0.5">
                  @-mention teammates for priority alerts
                </p>
              </div>
              <div className="flex-1 min-h-0 p-3">
                <TaskChatPanel taskId={task.id} fill={true} />
              </div>
            </aside>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
